import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  Paper,
} from "@mui/material";
import { Talent } from "../../features/talents/talentsTypes";
import { useTranslation } from "react-i18next";
import TalentAvatar from "./TalentAvatar";
import {
  getMaskedTalentName,
  getNameInitial,
  useShouldMaskTalentIdentity,
} from "src/utils/talentPrivacy";

interface TalentTableProps {
  talents: Talent[];
  onRowClick: (talent: Talent, index: number) => void;
}

const TalentTable: React.FC<TalentTableProps> = ({ talents, onRowClick }) => {
  const { t } = useTranslation();
  const shouldMaskTalentIdentity = useShouldMaskTalentIdentity();
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "rejected":
        return t("filterBar.rejected");
      case "requested":
        return t("filterBar.requested");
      case "accepted":
        return t("filterBar.accepted");
      default:
        return status;
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel>{t("filterBar.searchName")}</TableSortLabel>
            </TableCell>
            <TableCell align="right">{t("talents.position")}</TableCell>
            <TableCell align="right">
              <TableSortLabel>{t("filterBar.status")}</TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel>{t("filterBar.score", { min: 0, max: 100 })}</TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel>{t("filterBar.sortOptions.appliedDate")}</TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {talents.map((talent, index) => {
            const displayName = shouldMaskTalentIdentity
              ? getMaskedTalentName(talent.firstName, talent.lastName)
              : `${talent.firstName} ${talent.lastName}`.trim();

            return (
            <TableRow
              key={talent.id}
              hover
              onClick={() => onRowClick(talent, index)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell
                component="th"
                scope="row"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TalentAvatar
                  profileId={talent.id}
                  avatarId={talent.avatarId}
                  fallbackText={
                    shouldMaskTalentIdentity
                      ? getMaskedTalentName(talent.firstName, talent.lastName)
                      : talent.firstName?.charAt(0)
                  }
                  blurred={shouldMaskTalentIdentity}
                  sx={{ mr: 2 }}
                />
                <Typography variant="body1">
                  {displayName || getNameInitial(talent.firstName)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {talent.positionTitle || talent.headline}
              </TableCell>
              <TableCell align="right">{getStatusLabel(talent.status)}</TableCell>
              <TableCell align="right">{talent.completeness}</TableCell>
              <TableCell align="right">
                {new Date(talent.createdAt).toLocaleDateString()}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TalentTable;
